import arrayMutators from 'final-form-arrays';
import React, { useCallback, useMemo } from 'react';
import { Form as BaseForm } from 'react-final-form';

import { alertError, alertSuccess } from '@dashboard/contexts/SnackbarManager/store';
import buildError from '@dashboard/errors';

type TBaseFormProps = Parameters<typeof BaseForm>[0];
type Props = {
  children: JsxNode;
  className?: string;
  id?: string;
  successMessage?: string;
  initialValues?: TBaseFormProps['initialValues'];

  // We set the form value to any so that we can cast it directly to the submit form
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmit: (v: any) => void;
};

export default function Form({ children, className, id, initialValues, successMessage, onSubmit }: Props): JsxNode {
  const mutators = useMemo(() => ({ ...arrayMutators }), []);
  const onSubmitHandler = useCallback(
    async (v: unknown) => {
      try {
        await onSubmit(v);
        if (successMessage) {
          alertSuccess(successMessage);
        }
      } catch (e) {
        const error = buildError(e);
        alertError(error.message);
      }
    },
    [successMessage, onSubmit]
  );
  // Using any to get around a `strictNullChecks` issue
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const castMutators = mutators as any;

  return (
    <BaseForm
      mutators={castMutators}
      initialValues={initialValues}
      onSubmit={onSubmitHandler}
      render={({ handleSubmit }) => (
        <form id={id} className={className} onSubmit={handleSubmit}>
          {children}
        </form>
      )}
    />
  );
}
